/**
 * presetTypes.ts
 *
 * This file defines types and enums for managing presets in the application.
 *
 * Features:
 * - Defines various preset types for local state, data scope, and platform.
 * - Provides type definitions for preset editor component props.
 * - Includes utility types for filtering and serializing presets.
 */

import type { SharedStateScope } from "@/components/ec/engine/hocs/withSharedState/sharedStateTypes";
import type { ModelName, PresetApplicationId } from "@/endlaFunctions/customTypings/ecTypes";
import type { ComponentType } from "react";

/** All-important enum representing different types of presets. */
export enum PresetType {
  // TODO: These just work. Now they should live in EC State, pretty much same as local state.
  // LocalState
  ColumnVisibility = "columnVisibility", // Ties directly into tanstack/react-table's column visibility (https://tanstack.com/table/v8/docs/guide/column-visibility)
  ColumnOrder = "columnOrder", // Ties directly into tanstack/react-table's column ordering (https://tanstack.com/table/v8/docs/guide/column-ordering)
  ColumnDisplay = "columnDisplay", // Merged column visibility and ordering - see issue #9517
  ColorScale = "colorScale",
  SizeScale = "sizeScale",
  ZScale = "zScale",
  ShapeScale = "shapeScale",
  OpacityScale = "opacityScale",
  BubbleMap = "bubbleMap",
  SceneScaleParameters = "sceneScaleParameters",
  ScenePathFinding = "scenePathFinding",
  GraphConfig = "graphConfig", // Preset for Endla Graph component (nodes/edges configuration)

  PipelineTopologyEditorConfig = "pipelineTopologyEditorConfig",

  // Data Scope
  // TODO: These should just become Local State presets. We have 2 options, either make them punlishable. In which case fitlers can be wired to downstreams ECs.
  // TODO: Otherwise, we can just have filters live in the EC's state, and then the output port is the filtered Rows only. Either (or Both??) works.
  Filters = "filters",
  SceneObjectsVisibility = "sceneObjectsVisibility",

  // TODO: These now live in the platform level control surface. Think stays similar to existing.
  // Platform
  DashboardFilters = "dashboardFilters",
  DagAssetFilters = "dagAssetFilters",
}

/** Type representing filtering preset types. */
export type FilteringPresetType =
  | PresetType.DashboardFilters
  | PresetType.DagAssetFilters
  | PresetType.Filters;

/** EC-level filter preset type (stored in EC local state, not platform). */
export type ECFilterPresetType = PresetType.DagAssetFilters;

/** Type representing scope-based filtering preset types (excludes standalone DAG filters). */
export type ScopeFilteringPresetType = PresetType.DashboardFilters | PresetType.Filters;

/** Type representing the local state of a preset. */
export type PresetLocalState = {
  selectedPreset: PrimaryKey | null;
  isEdited: boolean;
};

/**
 * Identifies whether user inspects preset via standalone editor (i.e.
 * by opening visibility menu popover) or in presets manager dialog
 */
export enum PresetEditorMountType {
  ManagerDialog = "presetManagerDialog",
  StandaloneEditor = "standaloneEditor",
}

/** Props for the preset editor component - see README.md. */
export type PresetEditorComponentProps<TPresetData> = {
  /** Shared state scope, with details about the dataState, resolvedVariableAtoms, etc. */
  scope: SharedStateScope | null;
  /** Name of the model/table for which the preset is being edited (eg: "zone"). */
  modelName: ModelName;
  /** Current value of the preset being edited, */
  value: TPresetData;
  /** Application id. Example: "default". */
  applicationId: PresetApplicationId;
  /** Callback to update the preset value. */
  onChange: (value: TPresetData | ((prev: TPresetData) => TPresetData)) => void;
  /** Type of the preset being edited. Eg: "columnVisibility", "colorScale", etc. */
  presetType: PresetType;
  /** The mount type of the preset editor ("standaloneEditor" | "presetManagerDialog"). */
  presetMountType: PresetEditorMountType;
  onApplicationIdChange?: (nextApplicationId: PresetApplicationId) => void;
  /** Flag to indicate if the component should be rendered in condensed mode. */
  isCondensed: boolean;
  /** Clears the settings for the current application. */
  clearApplication?: (applicationId: PresetApplicationId) => void;
};

export type PresetEditorComponent<TPresetData> = ComponentType<
  PresetEditorComponentProps<TPresetData>
>;

export type ColorScaleColumnKeyPickerProps = {
  modelName: ModelName;
  columnKey: string | undefined;
  onColumnKeyChange: (nextColumnKey: string | null) => void;
  isBreadcrumbs: boolean;
  isClearable?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
};

export type PlatformPresetType = PresetType.DashboardFilters;

export type ScopePresetType =
  | PresetType.Filters
  | PresetType.SceneObjectsVisibility
  | PresetType.ScenePathFinding;

export type LocalPresetType = Exclude<PresetType, ScopePresetType | PlatformPresetType>;

export type PresetTypeWithScope = ScopePresetType | LocalPresetType;
